/**
 * Tours — one guided tour per dashboard page.
 *
 * Same shape as Páginas: cards in a collapsible section per BU. Bumping a revision re-shows the
 * tour to everyone, including people who already saw the previous one, and resets its reach.
 *
 * @module Pages/BUs/Tours
 */

import { css } from '@emotion/css';
import { UTButton, UTLoading, UTSelect } from '@group-link-one/gl-ui/utilities';
import { useMemo, useState } from 'react';
import type { JSX } from 'react';
import { useBumpTourVersion } from '../../Hooks/useBumpTourVersion';
import { BumpTourModal } from '../BumpTourModal/BumpTourModal';
import { useDeleteTour } from '../../Hooks/useDeleteTour';
import { useTours } from '../../Hooks/useTours';
import type { Tour } from '../../Types/Tours.types';
import { groupToursByTenant } from '../../Utils/groupToursByTenant';
import { DeleteTourModal } from '../DeleteTourModal/DeleteTourModal';
import { TourCard } from '../TourCard/TourCard';
import { TourFormModal } from '../TourFormModal/TourFormModal';
import { TourStatsModal } from '../TourStatsModal/TourStatsModal';
import { usePrivileges } from '@/Hooks/usePrivileges/usePrivilleges';
import { useTenants } from '@/Hooks/useTenants';
import { getTenantColor, getTenantIcon } from '@/Shared/brand';
import { CollapsibleSection } from '@/Shared/components/CollapsibleSection/CollapsibleSection';
import { EmptyState } from '@/Shared/components/EmptyState/EmptyState';
import { MainContent } from '@/Templates/Main/MainContent/MainContent';
import { TruncatedListNotice } from '@/Shared/components/TruncatedListNotice/TruncatedListNotice';
import { isListTruncated } from '@/Shared/constants/listLimits';

const screenStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-base);
`;

const toolbarStyle = css`
  display: flex;
  align-items: flex-start;
  gap: var(--space-base);
  flex-wrap: wrap;
`;

const descriptionStyle = css`
  margin: 0;
  max-width: 620px;
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  text-wrap: pretty;
`;

const spacerStyle = css`
  flex: 1;
`;

const filterStyle = css`
  min-width: 200px;
`;

const groupsStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-ml);
`;

/**
 * Wider minimum than Páginas: a tour card's footer carries the "Subir revisão" button next to
 * three icon buttons, which does not fit on one line below ~320px.
 */
const cardsGridStyle = css`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(320px, 100%), 1fr));
  gap: var(--space-base);
`;

const ALL_TENANTS = 'all';

const ToursList = (): JSX.Element => {
  const { pages: privileges } = usePrivileges();
  const { tenants, tenantOptions } = useTenants();

  const [tenantFilter, setTenantFilter] = useState<string>(ALL_TENANTS);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [statsTour, setStatsTour] = useState<Tour | undefined>();
  const [deletingTour, setDeletingTour] = useState<Tour | undefined>();
  /** `{}` opens the modal for a new tour; `{ tour }` for an existing one. */
  const [editing, setEditing] = useState<{ tour?: Tour } | undefined>();
  const [bumping, setBumping] = useState<Tour | undefined>();

  const { data, isLoading } = useTours();
  const bumpVersion = useBumpTourVersion();
  const deleteTour = useDeleteTour();

  const allTours = useMemo(() => data?.data ?? [], [data]);

  const groups = useMemo(() => {
    const scoped =
      tenantFilter === ALL_TENANTS
        ? allTours
        : allTours.filter(tour => tour.tenantSlug === tenantFilter);

    return groupToursByTenant(scoped);
  }, [allTours, tenantFilter]);

  const filterOptions = useMemo(
    () => [
      { id: ALL_TENANTS, text: 'Todos os tenants', value: ALL_TENANTS },
      ...tenantOptions.map(option => ({
        id: String(option.id),
        text: option.text,
        value: String(option.value),
      })),
    ],
    [tenantOptions],
  );

  if (isLoading) {
    return (
      <MainContent id='tours-main' styling={{ noBackgroundColor: true, noBorder: true }}>
        <UTLoading type='dots' />
      </MainContent>
    );
  }

  return (
    <MainContent id='tours-main' styling={{ noBackgroundColor: true, noBorder: true }}>
      <div className={screenStyle}>
        <div className={toolbarStyle}>
          <p className={descriptionStyle}>
            Um tour por página. Subir a revisão reexibe o tour para todo mundo, inclusive para quem
            já viu a revisão anterior.
          </p>
          <div className={spacerStyle} />
          <div className={filterStyle}>
            <UTSelect
              options={filterOptions}
              canSelectMultiple={false}
              hasCheckbox={false}
              defaultSelectedOptions={[filterOptions[0]]}
              inputOptions={{ textFieldProps: { placeholder: 'Todos os tenants' } }}
              onSelectedOptionsChange={options => {
                setTenantFilter(options.length > 0 ? String(options[0].value) : ALL_TENANTS);
              }}
            />
          </div>

          {isListTruncated(data?.pagination.total, allTours.length) ? (
            <TruncatedListNotice
              id='tours-truncated'
              shown={allTours.length}
              total={data?.pagination.total ?? allTours.length}
              noun='tours'
            />
          ) : null}
          {privileges.BUSINESS_UNITS.TOURS.CREATE ? (
            <UTButton
              fitContent
              id='tours-create'
              text='Criar tour'
              icon='Arrows.Plus'
              onClick={() => {
                setEditing({});
              }}
            />
          ) : null}
        </div>

        {groups.length === 0 ? (
          <EmptyState
            id='tours-empty'
            icon='Misc.Broadcast'
            title='Nenhum tour cadastrado'
            description='Um tour apresenta uma página do dashboard na primeira visita. Os passos ficam no código do dashboard; aqui você escolhe a página e a revisão.'
            action={
              privileges.BUSINESS_UNITS.TOURS.CREATE ? (
                <UTButton
                  fitContent
                  text='Criar tour'
                  icon='Arrows.Plus'
                  onClick={() => {
                    setEditing({});
                  }}
                />
              ) : undefined
            }
          />
        ) : (
          <div className={groupsStyle}>
            {groups.map(group => {
              const tenant = tenants.find(item => item.slug === group.tenantSlug);

              return (
                <CollapsibleSection
                  key={group.tenantSlug}
                  id={`tours-section-${group.tenantSlug}`}
                  title={group.tenantSlug}
                  monoTitle
                  icon={getTenantIcon({ slug: group.tenantSlug, iconName: tenant?.iconName })}
                  iconColor={getTenantColor(group.tenantSlug)}
                  count={group.countLabel}
                  summary={group.activeLabel}
                  open={!collapsed[group.tenantSlug]}
                  onToggle={() => {
                    setCollapsed(current => ({
                      ...current,
                      [group.tenantSlug]: !current[group.tenantSlug],
                    }));
                  }}
                >
                  <div className={cardsGridStyle}>
                    {group.tours.map(tour => (
                      <TourCard
                        key={tour.id}
                        tour={tour}
                        viewCount={tour.viewerCount}
                        canBump={privileges.BUSINESS_UNITS.TOURS.SUBMIT}
                        canEdit={privileges.BUSINESS_UNITS.TOURS.EDIT}
                        canDelete={privileges.BUSINESS_UNITS.TOURS.DELETE}
                        onBump={target => {
                          setBumping(target);
                        }}
                        onStats={setStatsTour}
                        onEdit={target => {
                          setEditing({ tour: target });
                        }}
                        onDelete={setDeletingTour}
                      />
                    ))}
                  </div>
                </CollapsibleSection>
              );
            })}
          </div>
        )}
      </div>

      {statsTour ? (
        <TourStatsModal
          open
          tour={statsTour}
          onClose={() => {
            setStatsTour(undefined);
          }}
        />
      ) : null}

      {/* UTModal keeps its content in the a11y tree when closed, so mount it only when needed. */}
      {bumping ? (
        <BumpTourModal
          open
          tour={bumping}
          viewCount={bumping.viewerCount}
          isBumping={bumpVersion.isPending}
          error={bumpVersion.error?.message ?? null}
          onClose={() => {
            bumpVersion.reset();
            setBumping(undefined);
          }}
          onConfirm={target => {
            bumpVersion.mutate(target.id, {
              onSuccess: () => {
                setBumping(undefined);
              },
            });
          }}
        />
      ) : null}

      {editing ? (
        <TourFormModal
          open
          tour={editing.tour}
          onClose={() => {
            setEditing(undefined);
          }}
        />
      ) : null}

      {deletingTour ? (
        <DeleteTourModal
          open
          tour={deletingTour}
          viewCount={deletingTour.viewerCount}
          isDeleting={deleteTour.isPending}
          onClose={() => {
            setDeletingTour(undefined);
          }}
          onConfirm={target => {
            deleteTour.mutate(target.id, {
              onSuccess: () => {
                setDeletingTour(undefined);
              },
            });
          }}
        />
      ) : null}
    </MainContent>
  );
};

export { ToursList };
